import { useListContext } from 'react-admin';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { isAuthorizationError } from '@src/apis/reware/utils';

/**
 * What the Orders list renders instead of a datagrid when the query fails.
 *
 * The denial case is called out separately because it is reachable by ordinary configuration,
 * not only by breakage. The sidebar entry is gated on `config_portal_app_catalog` while the
 * server gates the data on `admin_orders_portal`, and the two resolve to different role sets —
 * so a user can hold catalog access and still be refused orders. Left to react-admin's default
 * the page renders an empty datagrid over an error toast, which reads as "this partner has no
 * orders".
 */
const OrderListError = () => {
  const { error } = useListContext();

  if (isAuthorizationError(error)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <AlertTitle>You don&apos;t have access to orders</AlertTitle>
          <Typography variant="body2">
            Your account can reach this page but isn&apos;t permitted to read order data for this
            partner. Ask your Trove representative for orders portal access, or switch to a
            partner where you already have it.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">
        <AlertTitle>Couldn&apos;t load orders</AlertTitle>
        <Typography variant="body2">
          Something went wrong fetching orders. Try again in a moment — if it keeps happening,
          contact your Trove representative.
        </Typography>
      </Alert>
    </Box>
  );
};

export default OrderListError;
